import { FC, PropsWithChildren, ReactNode, useCallback, useRef } from 'react'

import { usePopupPosition } from '../../../hooks/usePopupPosition'
import { useOnClickedOutside } from '../../../utils/useOnClickedOutside'
import { Direction } from '../../common/types'
import { DropdownContent } from './DropdownContent'

export interface DropdownProps {
  direction?: Direction
  distance?: string
  isPagination?: boolean
  isMultiSelect?: boolean
  isOpen: boolean
  content: ReactNode
  setIsOpen: (isOpen: boolean) => void
}

export const Dropdown: FC<PropsWithChildren<DropdownProps>> = (props) => {
  const {
    children,
    direction = 'top',
    distance = '1rem',
    isOpen,
    isMultiSelect,
    isPagination,
    content,
    setIsOpen,
  } = props

  const containerRef = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [setIsOpen])

  const handleOutsideClick = useCallback(() => {
    if (isOpen) {
      onClose()
    }
  }, [onClose, isOpen])

  const triggerRef = useOnClickedOutside<HTMLDivElement>(
    handleOutsideClick,
    containerRef.current ? [containerRef.current] : []
  )

  const { contentOrigin } = usePopupPosition({
    isOpen,
    boxRef: containerRef,
    triggerRef: triggerRef,
    contentDirection: direction,
    distance: '1rem',
  })

  const triggerWidth = triggerRef?.current?.getBoundingClientRect().width

  const dropdownContentStyles = {
    minWidth: triggerWidth,
    top: contentOrigin?.top,
    left: contentOrigin?.left,
    transform: contentOrigin?.transform,
    ...(isPagination &&
      direction === 'top' && {
        top: 0,
        left: 'unset',
        transform: 'translate(0%, calc(-100% - 1rem))',
      }),
  }

  return (
    <div ref={triggerRef} onClick={() => !isMultiSelect && setIsOpen(!isOpen)}>
      {children}
      <DropdownContent
        contentOrigin={contentOrigin}
        isOpen={isOpen}
        onClose={onClose}
        ref={containerRef}
        style={dropdownContentStyles}
        isPagination={isPagination}
      >
        {content}
      </DropdownContent>
    </div>
  )
}
